import { formatDistanceToNow } from "date-fns";
import { Check, ChevronDown } from "lucide-react";
import { type FC, useState } from "react";

import { cn } from "@/lib/utils";
import { m } from "@recobra/i18n/messages";
import type { NotificationInboxItem } from "@recobra/schemas/notification";
import { Button } from "@recobra/ui/button";

import { useLocale } from "../lib";
import { useTaskName } from "../tasks";
import { formatNotificationContent } from "./formatNotificationContent";
import { formatNotificationSource } from "./formatNotificationSource";
import { notificationDateLocale } from "./notificationDateLocale";
import { notificationIcon } from "./notificationIcons";
import {
  type NotificationNavigationTarget,
  resolveNotificationTarget,
} from "./resolveNotificationNavigation";

export const NotificationListItem: FC<{
  item: NotificationInboxItem;
  onMarkRead?: (id: string) => void;
  onNavigate?: (target: NotificationNavigationTarget, id: string) => void;
}> = ({ item, onMarkRead, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  const { locale } = useLocale();
  const taskName = useTaskName();
  const content = formatNotificationContent(item, taskName);
  const source = formatNotificationSource(item);
  const target = resolveNotificationTarget(item);
  const relativeTime = formatDistanceToNow(new Date(item.createdAt), {
    addSuffix: true,
    locale: notificationDateLocale(locale),
  });
  const { Icon, className } = notificationIcon(item);

  const toggleExpanded = () => setExpanded((value) => !value);

  const openAction = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    if (!target) return;
    onNavigate?.(target, item.id);
  };

  return (
    <div
      className={cn(
        "border-b px-4 py-3 transition-colors last:border-b-0",
        expanded ? "bg-muted/20" : "hover:bg-muted/10",
      )}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={cn("flex size-8 shrink-0 items-center justify-center rounded-full", className)}
        >
          <Icon className="size-3.5" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <button
            type="button"
            className="w-full text-left"
            onClick={target ? openAction : toggleExpanded}
            aria-expanded={target ? undefined : expanded}
          >
            <div className="flex min-w-0 items-center gap-1.5">
              <p
                className={cn(
                  "truncate text-sm",
                  item.isUnread
                    ? "font-semibold text-foreground"
                    : "font-medium text-foreground/90",
                )}
              >
                {content.title}
              </p>
              {item.isUnread ? (
                <span className="size-2 shrink-0 rounded-full bg-info" aria-hidden />
              ) : null}
            </div>

            <p
              className={cn(
                "mt-0.5 text-muted-foreground text-xs leading-5",
                !expanded && "line-clamp-1",
              )}
            >
              {content.message}
            </p>
          </button>

          <div className="mt-1.5 flex flex-col gap-1.5">
            {expanded && content.actionLabel && target ? (
              <Button
                type="button"
                variant="link"
                size="xs"
                className="self-start"
                onClick={openAction}
              >
                {content.actionLabel}
              </Button>
            ) : null}

            <p className="text-xs text-muted-foreground/80">
              {source}
              <span className="px-1">·</span>
              {relativeTime}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-start gap-0.5">
          {item.isUnread ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="shrink-0"
              aria-label={m.notifications_mark_read()}
              onClick={(event) => {
                event.stopPropagation();
                onMarkRead?.(item.id);
              }}
            >
              <Check />
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="shrink-0"
            aria-label={m.notifications_toggle_details()}
            aria-expanded={expanded}
            onClick={(event) => {
              event.stopPropagation();
              toggleExpanded();
            }}
          >
            <ChevronDown className={cn("transition-transform", expanded && "rotate-180")} />
          </Button>
        </div>
      </div>
    </div>
  );
};
